<script setup lang="ts">
import Icon from '~/components/global/Icon.vue'
import type { EstateListingFaqItem } from '~/utils/warehouseListingContent'

const props = defineProps<{
  title: string
  items: EstateListingFaqItem[]
}>()

useSchemaOrg([
  defineWebPage({
    '@type': 'FAQPage',
  }),
  ...props.items.map((item) =>
    defineQuestion({
      name: item.question,
      acceptedAnswer: item.answer,
    })
  ),
])

function onAnswerClick(event: MouseEvent) {
  const link = (event.target as HTMLElement).closest('a')
  const href = link?.getAttribute('href')

  if (
    !href?.startsWith('/') ||
    link?.target === '_blank' ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey
  ) {
    return
  }

  event.preventDefault()
  navigateTo(href)
}
</script>

<template>
  <section class="c-estate-listing-faq">
    <h2 class="c-estate-listing-faq__title">{{ title }}</h2>

    <details
      v-for="item in items"
      :key="item.question"
      class="c-estate-listing-faq__item"
    >
      <summary class="c-estate-listing-faq__question">
        <h3>{{ item.question }}</h3>
        <Icon name="chevron-down" />
      </summary>

      <!-- eslint-disable vue/no-v-html -->
      <div
        class="c-estate-listing-faq__answer"
        @click="onAnswerClick"
        v-html="item.answer"
      />
      <!-- eslint-enable vue/no-v-html -->
    </details>
  </section>
</template>

<style lang="scss" scoped>
@use '@inkline/inkline/css/mixins' as *;

.c-estate-listing-faq {
  &__title {
    margin-bottom: 24px;
  }

  &__item {
    border-bottom: 1px solid var(--color-border);

    &:first-of-type {
      border-top: 1px solid var(--color-border);
    }
  }

  &__question {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 20px 0;
    cursor: pointer;
    list-style: none;

    &::-webkit-details-marker {
      display: none;
    }

    h3 {
      margin: 0;
      font-size: 18px;
      line-height: 1.4;

      @include breakpoint-down('md') {
        font-size: 16px;
      }
    }

    @media (hover: hover) {
      &:hover h3 {
        text-decoration: underline;
      }
    }

    .ico {
      display: flex;
      flex-shrink: 0;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      font-size: 12px;
      transition: transform 0.2s ease;
    }
  }

  &__item[open] .ico {
    transform: rotate(180deg);
  }

  &__answer {
    padding-bottom: 20px;

    :deep(p) {
      margin: 0;
    }

    :deep(p + p) {
      margin-top: 1rem;
    }

    :deep(a) {
      color: var(--color-primary);
      font-weight: 600;
      text-decoration: underline;
    }
  }
}
</style>
